<template>
  <div v-if="propositions.length > 0" class="block-container">
    <h2 class="block-title">
      {{ usedIn === 'seo-landing-page' ? customTitle : content.BlockTitle }}
    </h2>
    <Breadcrumbs :location-path="locationPath" :link-last="false" />
    <div class="propositions">
      <router-link
        v-for="proposition in propositions"
        :key="proposition.ad_id"
        :to="$routes.PROPOSITION + '/' + proposition.ad_id"
        class="proposition"
      >
        <div class="proposition-image">
          <img v-if="proposition.filename" :src="proposition.filename" alt="" />
          <div v-else class="no-image"></div>
        </div>
        <div class="proposition-content">
          <h3 class="address">{{ proposition.streetAddress }}</h3>
          <h4 class="area">{{ proposition.areaName }}</h4>
          <div class="footer">
            <span class="rooms"
              >{{ proposition.num_rooms }} {{ $tc('general_rooms_short') }} ∙
            </span>
            <span class="sqm"
              >{{ proposition.square_meter }}
              {{ $t('general_sqm_short') }}</span
            >
            <div class="rent">
              <span class="price">{{ proposition.rent }}</span>
              {{ $t('general_currency_per_month_short') }}
            </div>
          </div>
        </div>
      </router-link>
    </div>
    <ul class="pagination">
      <li v-for="n in pageRange" :key="n">
        <span v-if="currentPage == n">{{ n }}</span>
        <a v-else-if="n === 1" :href="firstPageUrl">{{ n }}</a>
        <a v-else :href="`?page=${n}`">{{ n }}</a>
      </li>
      <li
        v-if="
          Math.ceil(totalCount / pageSize) > pageRange[pageRange.length - 1]
        "
      >
        <a :href="`?page=${pageRange[pageRange.length - 1] + 1}`">...</a>
      </li>
    </ul>
  </div>
</template>

<script>
import Breadcrumbs from '@/components/ContentBlocks/Breadcrumbs.vue';
import { lbApiBeta } from '../../utils/axiosConfig';

export default {
  name: 'Propositions',

  components: {
    Breadcrumbs
  },

  props: {
    content: {
      type: [Object, Array],
      default: () => {}
    },
    usedIn: {
      type: String,
      default: ''
    },
    locationPath: {
      type: Array,
      default: () => []
    },
    areaName: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      pageSize: 16,
      propositions: [],
      totalCount: 0,
      currentPage: 0,
      resultsOutsideLocation: false
    };
  },

  computed: {
    firstPageUrl() {
      const url = new URL(window.location.href);
      url.searchParams.delete('page');
      return url.pathname + url.search;
    },
    customTitle() {
      return this.resultsOutsideLocation
        ? this.$t('sitemap_properties_nearby', { area: this.areaName })
        : this.$t('sitemap_properties_in', { area: this.areaName });
    },
    pageRange() {
      const page = parseInt(this.currentPage);
      const totalPages = Math.ceil(this.totalCount / this.pageSize);
      if (totalPages <= 1) {
        return [];
      }

      if (this.totalCount > 10 && page > 5) {
        let pages = [];
        let p = parseInt(page) - 5;
        for (let x = 0; x < 10; x++) {
          if (p <= totalPages) {
            pages.push(p++);
          }
        }
        return pages;
      }

      return Array.from({ length: Math.min(11, totalPages) }, (_, i) => i + 1);
    }
  },

  created() {
    if (this.usedIn === 'seo-landing-page') {
      this.propositions = this.content.result;
      this.totalCount = this.content.totalCount;
      this.currentPage = this.content.currentPage;
      this.resultsOutsideLocation = this.content.resultsOutsideLocation;
    } else {
      this.getPropositions();
    }
  },

  methods: {
    async getPropositions() {
      if (!this.content.AreaID) return;

      try {
        const result = await lbApiBeta.get(
          `/api/Inspiration/GetStartPageInspirationPictures?areaId=${this.content.AreaID}&count=8`
        );

        this.propositions = result.data.Object;
        return result.data;
      } catch (error) {
        //
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.block-container {
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
  margin-top: 32px;
  margin-bottom: 32px;
  padding: 0 16px;

  @media ($large-screen) {
    margin-top: 64px;
    margin-bottom: 64px;
  }
}

.block-title {
  font-size: 24px;
  margin-bottom: 0px;

  @media ($large-screen) {
    font-size: 30px;
  }
}

.propositions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 10px;
  font-family: $font-body;

  @media ($large-screen) {
    grid-template-columns: 1fr 1fr 1fr 1fr;
    grid-gap: 16px;
  }
}

.proposition {
  margin-bottom: 10px;
  color: var(--Text-Primary);
}

.proposition-image {
  display: flex;
  aspect-ratio: 16/9;
  border-radius: 6px;
  margin-bottom: 6px;
  overflow: hidden;

  @media ($large-screen) {
    border-radius: 8px;
    margin-bottom: 10px;
  }
}

.proposition-image img {
  width: 100%;
  object-fit: cover;
}

.proposition-image .no-image {
  background-color: #dddcdb;
  background-size: 50%;
  background-image: url('../../assets/svg/skyline-placeholder-img.svg');
  height: 100%;
  width: 100%;
  background-position: 50%;
}

.address {
  font-size: 14px;
  line-height: 18px;
  font-weight: 700;
  margin: 0;
  margin-bottom: 1px;

  @media ($large-screen) {
    font-size: 17px;
    margin-bottom: 3px;
  }
}

.area {
  font-size: 12px;
  line-height: 15px;
  font-weight: 400;
  margin-bottom: 2px;
  margin: 0;

  @media ($large-screen) {
    font-size: 13px;
  }
}

.footer {
  font-size: 13px;
  font-weight: 400;
  margin-top: 5px;

  @media ($large-screen) {
    font-size: 14px;
    margin-top: 6px;
  }
}

.price {
  font-weight: 600;
}

.pagination {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr;
  grid-gap: 10px;
  list-style: none;
  margin: 15px 0px;
  padding: 0;
  @media ($large-screen) {
    display: flex;
    flex-direction: row;
  }
}

.pagination li {
  margin-right: 5px;
}

.pagination li a {
  font-size: 12px;
  font-weight: bold;
  padding: 6px 14px;
  border: 1px solid rgba(0, 0, 0, 0);
  border-radius: 5px;
}

.pagination li a:hover {
  border: 1px solid #aaa;
  border-radius: 5px;
}

.pagination li span {
  font-size: 12px;
  font-weight: bold;
  padding: 6px 14px;
  border: 1px solid #aaa;
  border-radius: 5px;
}
</style>
